import { useState, useEffect, useRef } from 'react';

// 模擬：3.4萬/月換算每秒收益
const MONTHLY_RENT = 34000;
const SECONDS_PER_MONTH = 30 * 24 * 3600;
const PER_SECOND = MONTHLY_RENT / SECONDS_PER_MONTH; // ≈ 0.01312 元/秒
const BASE_INCOME = 2140;
const MAX_INCOME = 30_000_000; // 防爆：3000萬歸零

function AnimatedNumber({ value }: { value: number }) {
  const parts = value.toFixed(4).split('.');
  const intPart = parseInt(parts[0]).toLocaleString('zh-TW');
  const decPart = parts[1] ?? '0000';
  return (
    <span className="tabular-nums">
      {intPart}
      <span className="opacity-50">.{decPart}</span>
    </span>
  );
}

export default function RentalIncomeCounter() {
  const [display, setDisplay] = useState(BASE_INCOME);
  const startRef = useRef(Date.now());
  const rafRef = useRef<number>(0);

  useEffect(() => {
    const tick = () => {
      const elapsed = (Date.now() - startRef.current) / 1000;
      const raw = BASE_INCOME + elapsed * PER_SECOND;
      setDisplay(raw % MAX_INCOME); // 超過 3000 萬自動歸零
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  return (
    <div className="w-full mt-8 mb-2 relative overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 border border-white/10">
      {/* 背景光暈 */}
      <div className="absolute -top-10 -left-10 w-48 h-48 bg-teal-500/15 rounded-full blur-3xl pointer-events-none"></div>
      <div className="absolute -bottom-10 -right-10 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>

      <div className="relative z-10 px-6 py-6 text-center">
        {/* 標題 */}
        <div className="flex items-center justify-center gap-2 mb-4">
          <div className="w-1.5 h-1.5 bg-teal-400 rounded-full animate-pulse"></div>
          <span className="text-gray-400 text-xs">月租金即時收益模擬（NT$ 34,000 / 月）</span>
        </div>

        {/* 主數字 */}
        <div className="flex items-baseline justify-center gap-2 mb-3">
          <span className="text-teal-400 text-xl font-bold">$</span>
          <span className="text-4xl md:text-5xl font-black text-white tracking-tight leading-none">
            <AnimatedNumber value={display} />
          </span>
          <span className="text-gray-400 text-base font-medium">元</span>
        </div>

        {/* 每秒速率 */}
        <div className="flex items-center justify-center gap-2">
          <div className="w-1.5 h-1.5 bg-teal-400 rounded-full animate-ping"></div>
          <p className="text-teal-400/80 text-xs font-medium">
            每秒 +{PER_SECOND.toFixed(4)} 元
          </p>
        </div>
      </div>
    </div>
  );
}
